"use client";

import { Lock, User, Users } from "lucide-react";
import {
  AeraBusinessOptionsGrid,
  type AeraBusinessOption,
  type AeraBusinessType,
} from "@/app/components/AeraBusinessOptionsGrid";
import { AeraButton } from "@/app/components/ui/AeraButton";
import { AeraFieldLabel } from "@/app/components/ui/AeraFieldLabel";

export function WorkspaceScalePicker({
  value,
  onChange,
}: {
  value: string;
  onChange: (scale: "Solo" | "Team") => void;
}) {
  const isSolo = value === "Solo";
  const isTeam = value === "Team";

  return (
    <div className="space-y-2">
      <AeraFieldLabel>ส่วนตัวหรือทีม?</AeraFieldLabel>
      <div className="grid grid-cols-2 gap-2">
        <AeraButton
          variant="disabled"
          type="button"
          fullWidth
          fontWeight="semibold"
          paddingSize="xxs"
          textSize="xs"
          onClick={() => onChange("Solo")}
          icon={
            <div
              className={[
                "flex h-10 w-10 shrink-0 items-center justify-center rounded-full border",
                isSolo
                  ? "border-aera-amber/35 bg-aera-amber/10"
                  : "bg-aera-lightgray/40",
              ].join(" ")}
            >
              <User
                className={[
                  "h-5 w-5",
                  isSolo ? "text-aera-charcoal" : "text-aera-gray",
                ].join(" ")}
                strokeWidth={2}
                aria-hidden
              />
            </div>
          }
          className={[
            "cursor-pointer py-2.5 justify-start! text-left!",
            isSolo
              ? "border-aera-amber/25! bg-aera-warmcream/40! text-aera-charcoal!"
              : "border-aera-lightgray! bg-aera-white! text-aera-gray! hover:border-aera-amber/50!",
          ].join(" ")}
        >
          <div className="min-w-0">
            <p
              className={[
                "leading-tight",
                isSolo ? "text-aera-charcoal" : "text-aera-gray",
              ].join(" ")}
            >
              ส่วนตัว
            </p>
            <p className="mt-1 text-xxs font-medium text-aera-gray">
              ใช้คนเดียว
            </p>
          </div>
        </AeraButton>
        <AeraButton
          variant="disabled"
          type="button"
          fullWidth
          fontWeight="semibold"
          paddingSize="xxs"
          textSize="xs"
          disabled
          title="เร็วๆ นี้"
          onClick={() => onChange("Team")}
          icon={
            <div
              className={[
                "flex h-10 w-10 shrink-0 items-center justify-center rounded-full",
                isTeam ? "bg-aera-amber/25" : "bg-aera-lightgray/40",
              ].join(" ")}
            >
              <Users
                className="h-5 w-5 text-aera-gray"
                strokeWidth={2}
                aria-hidden
              />
            </div>
          }
          className={[
            "cursor-not-allowed py-2.5 justify-start! text-left!",
            isTeam
              ? "border-aera-amber! bg-aera-warmcream! text-aera-charcoal!"
              : "border-aera-lightgray! bg-aera-white! text-aera-gray! hover:border-aera-amber/50!",
          ].join(" ")}
        >
          <div className="min-w-0">
            <p
              className={[
                "leading-tight",
                isTeam ? "text-aera-charcoal" : "text-aera-gray",
              ].join(" ")}
            >
              ทีม
            </p>
            <p className="mt-1 w-auto h-5 inline-flex items-center justify-center rounded-full border border-aera-amber bg-aera-amber/5 px-2 text-xxs font-medium text-aera-amber shadow-sm">
              เร็วๆ นี้
            </p>
          </div>
        </AeraButton>
      </div>
    </div>
  );
}

export function WorkspaceStatusPicker({
  value,
  onChange,
}: {
  value: string;
  onChange: (status: "active" | "inactive") => void;
}) {
  const isActive = value === "active";
  const isInactive = value === "inactive";

  return (
    <div className="space-y-2">
      <AeraFieldLabel>สถานะ:</AeraFieldLabel>
      <div className="grid grid-cols-2 gap-2">
        <AeraButton
          variant="disabled"
          type="button"
          fullWidth
          paddingSize="xxs"
          textSize="xs"
          fontWeight="semibold"
          onClick={() => onChange("active")}
          className={[
            "cursor-pointer py-2.5 hover:border-aera-amber!",
            isActive
              ? "border-aera-amber/25 bg-aera-warmcream/40 text-aera-charcoal"
              : "border-aera-lightgray bg-aera-white text-aera-gray hover:border-aera-amber/40",
          ].join(" ")}
        >
          เปิดใช้งาน
        </AeraButton>
        <AeraButton
          variant="disabled"
          type="button"
          fullWidth
          paddingSize="xxs"
          textSize="xs"
          fontWeight="semibold"
          onClick={() => onChange("inactive")}
          className={[
            "cursor-pointer py-2.5 hover:border-aera-amber!",
            isInactive
              ? "border-aera-amber/25 bg-aera-warmcream/40 text-aera-charcoal"
              : "border-aera-lightgray bg-aera-white text-aera-gray hover:border-aera-amber/40",
          ].join(" ")}
        >
          ปิดใช้งาน
        </AeraButton>
      </div>
    </div>
  );
}

export function WorkspaceCreateOptionsSection({
  selectedBusinessTypeId,
  onBusinessSelect,
  onClearError,
  scale,
  onScaleChange,
  status,
  onStatusChange,
  showStatusPicker = false,
  autoSelectDefault = false,
}: {
  selectedBusinessTypeId: AeraBusinessType | null;
  onBusinessSelect: (opt: AeraBusinessOption) => void;
  onClearError?: () => void;
  scale: string;
  onScaleChange: (scale: "Solo" | "Team") => void;
  status: string;
  onStatusChange: (status: "active" | "inactive") => void;
  /** แสดงตัวเลือกสถานะ — ใช้ใน modal สร้าง/ตั้งค่าเวิร์กสเปซ */
  showStatusPicker?: boolean;
  autoSelectDefault?: boolean;
}) {
  return (
    <section className="space-y-5">
      <AeraBusinessOptionsGrid
        selectedId={selectedBusinessTypeId}
        onSelect={onBusinessSelect}
        onClearError={onClearError}
        autoSelectDefault={autoSelectDefault}
      />
      <WorkspaceScalePicker value={scale} onChange={onScaleChange} />
      {showStatusPicker ? (
        <WorkspaceStatusPicker value={status} onChange={onStatusChange} />
      ) : null}
    </section>
  );
}

export function WorkspaceCreatePrivacyNote() {
  return (
    <p className="flex items-center justify-center gap-1.5 text-center text-xs text-aera-gray">
      <Lock className="h-3.5 w-3.5 shrink-0" strokeWidth={2} aria-hidden />
      ข้อมูลนี้ใช้เพื่อสร้างเวิร์กสเปซและตั้งค่า Bill-Lin เท่านั้น
    </p>
  );
}
